import React from 'react';
import { DIALOG_TYPE } from '../../../../constants';
import { AddCircleIcons, SearchIcons } from '../../../../icons/icons';
import { Button, Dialog, FloatingLabelInput } from '../../../../ui';
import { FrameworkPagination, JobTitlesGrid } from '../components';
import { SORT_OPTIONS } from '../constants';
import { useJobTitleManagement } from '../hooks';
import { JobTitleManagementProps, SortOption } from '../interfaces';

export const JobTitleManagementContainer = ({
  jobTitles = [],
}: JobTitleManagementProps) => {
  const {
    newJobTitle,
    setNewJobTitle,
    searchQuery,
    setSearchQuery,
    isDeleting,
    deleteModalOpen,
    jobTitleToDelete,
    currentPage,
    sortBy,
    setSortBy,
    itemsPerPage,
    sortedAndFilteredJobTitles,
    totalPages,
    paginatedJobTitles,
    handleAddJobTitle,
    handleDeleteJobTitle,
    openDeleteModal,
    closeDeleteModal,
    handlePageChange,
  } = useJobTitleManagement({ jobTitles });

  return (
    <div className='mt-4 space-y-4'>
      {/* Header & Controls Section */}
      <div className='bg-white rounded-lg border border-gray-200 shadow-sm'>
        <div className='p-4 pb-3 border-b border-gray-100'>
          <div className='flex items-center justify-between gap-4'>
            <h2 className='text-xl font-semibold text-gray-900'>
              Job Titles
              <span className='ml-2 text-sm font-normal text-gray-500'>
                ({sortedAndFilteredJobTitles.length.toLocaleString()})
              </span>
            </h2>
          </div>
        </div>

        <div className='p-4 flex items-end gap-3 bg-gray-50/50'>
          <div className='flex-1 max-w-md'>
            <div className='relative'>
              <SearchIcons className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' />
              <input
                  type='text'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='Search job titles...'
                  className='w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none bg-white'
              />
            </div>
          </div>
          <div className='flex gap-3 items-center'>
            <p className='text-sm text-gray-600'>Sort :</p>
            <div className='flex items-center'>
              <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className='px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent focus:outline-none bg-white'
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='flex-1 max-w-lg'>
            <FloatingLabelInput
                label=''
                value={newJobTitle}
                onChange={(e) => setNewJobTitle(e.target.value)}
                placeholder='Enter job title...'
            />
          </div>
          <Button
              onClick={handleAddJobTitle}
              className='bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-sm hover:shadow transition-all'
              icon={<AddCircleIcons className='w-4 h-4' />}
              hasIcon
              label='Add'
          />
        </div>
      </div>

      {/* Grid & Pagination Section */}
      <div className='bg-white rounded-lg border border-gray-200 shadow-sm'>
        <JobTitlesGrid
            jobTitles={paginatedJobTitles}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            searchQuery={searchQuery}
            onDelete={openDeleteModal}
        />

        <FrameworkPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={sortedAndFilteredJobTitles.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
        />
      </div>

      <Dialog
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          handleApply={handleDeleteJobTitle}
          title='Delete Job Title'
          confirmButtonLable={isDeleting ? 'Deleting...' : 'Delete'}
          cancelButtonLable='Cancel'
          dialogType={DIALOG_TYPE.ERROR}
          disabled={isDeleting}
      >
        <p>
          Are you sure you want to delete{' '}
          <strong>&ldquo;{jobTitleToDelete?.label}&rdquo;</strong>? This action
          cannot be undone.
        </p>
      </Dialog>
    </div>
  );
};
